"use client";

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent, MouseEvent } from "react";
import { useLinkPreviewVariant } from "./LinkPreviewContext";
import { LinkPreviewCardLayout } from "./OpenGraphPreview";
import MarketplaceCompactCta from "./marketplace/MarketplaceCompactCta";
import MarketplaceDataHealthIcon from "./marketplace/MarketplaceDataHealthIcon";
import MarketplaceFullFooter from "./marketplace/MarketplaceFullFooter";
import MarketplaceItemPreviewMediaLink from "./marketplace/MarketplaceItemPreviewMediaLink";
import { DEFAULT_MARKETPLACE_TITLE } from "./marketplace/MarketplaceItemPreviewCard.constants";
import type { MarketplaceItemPreviewCardProps } from "./marketplace/MarketplaceItemPreviewCard.types";
import { buildMarketplaceCtaLabel, getMarketplaceBrand, resolvePreviewHref } from "./marketplace/MarketplaceItemPreviewCard.utils";
import MarketplaceOverlayActionButtons from "./marketplace/MarketplaceOverlayActionButtons";
import { getMarketplaceContainerClass } from "./marketplace/previewLayout";
const normalizeSpaceSeparatedText = (value: string): string => value.trim().replace(/\s+/g, " ");
const isInteractiveElement = (target: EventTarget | null): boolean => target instanceof Element && target.closest("a, button, input, textarea, select, [role='button']") !== null;
const openResolvedHref = (resolvedPreviewHref: ReturnType<typeof resolvePreviewHref>): void => {
  if (resolvedPreviewHref.target === "_blank") {
    globalThis.window.open(resolvedPreviewHref.href, "_blank", "noopener,noreferrer");
    return;
  }
  globalThis.window.location.assign(resolvedPreviewHref.href);
};
const buildDisplayPriceParts = ({
  price,
  priceCurrency
}: {
  readonly price: string;
  readonly priceCurrency: string;
}) => {
  if (price.length === 0) {
    return {
      priceValueText: "",
      priceCurrencyText: "",
      ariaPriceText: ""
    };
  }
  let priceValueText = price;
  if (priceCurrency.length > 0) {
    const tokens = price.split(" ");
    const lastToken = tokens[tokens.length - 1];
    if (typeof lastToken === "string" && tokens.length > 1 && lastToken.toUpperCase() === priceCurrency.toUpperCase()) {
      const strippedPrice = tokens.slice(0, -1).join(" ").trim();
      if (strippedPrice.length > 0) {
        priceValueText = strippedPrice;
      }
    }
  }
  const ariaPriceText = priceCurrency.length > 0 ? `${priceValueText} ${priceCurrency}` : priceValueText;
  return {
    priceValueText,
    priceCurrencyText: priceCurrency,
    ariaPriceText
  };
};
export default function MarketplaceItemPreviewCard(t0) {
  const $ = _c(85);
  const {
    href,
    mediaUrl,
    mediaMimeType,
    price,
    priceCurrency,
    title,
    dataHealth,
    compact: t1,
    hideActions: t2
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const hideActions = t2 === undefined ? false : t2;
  const variant = useLinkPreviewVariant();
  let t3;
  if ($[0] !== href) {
    t3 = resolvePreviewHref(href);
    $[0] = href;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const resolvedPreviewHref = t3;
  let displayTitle;
  let hasPrice;
  let marketplaceBrand;
  let priceCurrencyText;
  let priceValueText;
  let t4;
  if ($[2] !== href || $[3] !== price || $[4] !== priceCurrency || $[5] !== title) {
    const normalizedPrice = typeof price === "string" ? normalizeSpaceSeparatedText(price) : "";
    const normalizedPriceCurrency = typeof priceCurrency === "string" ? normalizeSpaceSeparatedText(priceCurrency) : "";
    const {
      priceValueText: t5,
      priceCurrencyText: t6,
      ariaPriceText
    } = buildDisplayPriceParts({
      price: normalizedPrice,
      priceCurrency: normalizedPriceCurrency
    });
    priceValueText = t5;
    priceCurrencyText = t6;
    let t7;
    if ($[12] !== title) {
      t7 = typeof title === "string" ? title.trim() : "";
      $[12] = title;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    const normalizedTitle = t7;
    hasPrice = priceValueText.length > 0;
    const hasTitle = normalizedTitle.length > 0;
    displayTitle = hasTitle ? normalizedTitle : DEFAULT_MARKETPLACE_TITLE;
    marketplaceBrand = getMarketplaceBrand(href);
    t4 = buildMarketplaceCtaLabel({
      normalizedPrice: ariaPriceText,
      marketplaceBrand
    });
    $[2] = href;
    $[3] = price;
    $[4] = priceCurrency;
    $[5] = title;
    $[6] = displayTitle;
    $[7] = hasPrice;
    $[8] = marketplaceBrand;
    $[9] = priceCurrencyText;
    $[10] = priceValueText;
    $[11] = t4;
  } else {
    displayTitle = $[6];
    hasPrice = $[7];
    marketplaceBrand = $[8];
    priceCurrencyText = $[9];
    priceValueText = $[10];
    t4 = $[11];
  }
  const ctaAriaLabel = t4;
  const shouldShowDataHealthIcon = dataHealth !== undefined && dataHealth.state !== "fresh";
  let t5;
  if ($[14] !== resolvedPreviewHref) {
    t5 = event => {
      if (isInteractiveElement(event.target)) {
        return;
      }
      openResolvedHref(resolvedPreviewHref);
    };
    $[14] = resolvedPreviewHref;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleCardClick = t5;
  let t6;
  if ($[16] !== resolvedPreviewHref) {
    t6 = event_0 => {
      if (isInteractiveElement(event_0.target)) {
        return;
      }
      if (event_0.key !== "Enter") {
        return;
      }
      event_0.preventDefault();
      openResolvedHref(resolvedPreviewHref);
    };
    $[16] = resolvedPreviewHref;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleCardKeyDown = t6;
  let t7;
  if ($[18] !== ctaAriaLabel || $[19] !== handleCardClick || $[20] !== handleCardKeyDown) {
    t7 = {
      role: "link",
      tabIndex: 0,
      onClick: handleCardClick,
      onKeyDown: handleCardKeyDown,
      "aria-label": ctaAriaLabel
    };
    $[18] = ctaAriaLabel;
    $[19] = handleCardClick;
    $[20] = handleCardKeyDown;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const sharedCardProps = t7 as const;
  if (compact) {
    let t8;
    if ($[22] !== compact || $[23] !== variant) {
      t8 = getMarketplaceContainerClass(variant, compact);
      $[22] = compact;
      $[23] = variant;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    const t9 = `${t8} tw-relative`;
    let t10;
    if ($[25] !== dataHealth || $[26] !== shouldShowDataHealthIcon) {
      t10 = shouldShowDataHealthIcon && <MarketplaceDataHealthIcon dataHealth={dataHealth} />;
      $[25] = dataHealth;
      $[26] = shouldShowDataHealthIcon;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== mediaMimeType || $[29] !== mediaUrl || $[30] !== resolvedPreviewHref) {
      t11 = <MarketplaceItemPreviewMediaLink mediaMimeType={mediaMimeType} mediaUrl={mediaUrl} resolvedPreviewHref={resolvedPreviewHref} />;
      $[28] = mediaMimeType;
      $[29] = mediaUrl;
      $[30] = resolvedPreviewHref;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    const t12 = priceCurrencyText || undefined;
    let t13;
    if ($[32] !== ctaAriaLabel || $[33] !== hasPrice || $[34] !== hideActions || $[35] !== marketplaceBrand || $[36] !== priceValueText || $[37] !== resolvedPreviewHref || $[38] !== t12) {
      t13 = <MarketplaceCompactCta resolvedPreviewHref={resolvedPreviewHref} ctaAriaLabel={ctaAriaLabel} hideActions={hideActions} hasPrice={hasPrice} priceValueText={priceValueText} priceCurrencyText={t12} marketplaceBrand={marketplaceBrand} />;
      $[32] = ctaAriaLabel;
      $[33] = hasPrice;
      $[34] = hideActions;
      $[35] = marketplaceBrand;
      $[36] = priceValueText;
      $[37] = resolvedPreviewHref;
      $[38] = t12;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    let t14;
    if ($[40] !== hideActions || $[41] !== href || $[42] !== resolvedPreviewHref) {
      t14 = !hideActions && <MarketplaceOverlayActionButtons href={href} resolvedPreviewHref={resolvedPreviewHref} />;
      $[40] = hideActions;
      $[41] = href;
      $[42] = resolvedPreviewHref;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    let t15;
    if ($[44] !== sharedCardProps || $[45] !== t10 || $[46] !== t11 || $[47] !== t13 || $[48] !== t14 || $[49] !== t9) {
      t15 = <div {...sharedCardProps} className={t9} data-testid="manifold-item-card">{t10}{t11}{t13}{t14}</div>;
      $[44] = sharedCardProps;
      $[45] = t10;
      $[46] = t11;
      $[47] = t13;
      $[48] = t14;
      $[49] = t9;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== href || $[52] !== t15 || $[53] !== variant) {
      t16 = <LinkPreviewCardLayout href={href} variant={variant} hideActions={true}>{t15}</LinkPreviewCardLayout>;
      $[51] = href;
      $[52] = t15;
      $[53] = variant;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    return t16;
  }
  let t8;
  if ($[55] !== compact || $[56] !== variant) {
    t8 = getMarketplaceContainerClass(variant, compact);
    $[55] = compact;
    $[56] = variant;
    $[57] = t8;
  } else {
    t8 = $[57];
  }
  const t9 = `${t8} tw-relative`;
  let t10;
  if ($[58] !== dataHealth || $[59] !== shouldShowDataHealthIcon) {
    t10 = shouldShowDataHealthIcon && <MarketplaceDataHealthIcon dataHealth={dataHealth} />;
    $[58] = dataHealth;
    $[59] = shouldShowDataHealthIcon;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] !== mediaMimeType || $[62] !== mediaUrl || $[63] !== resolvedPreviewHref) {
    t11 = <MarketplaceItemPreviewMediaLink mediaMimeType={mediaMimeType} mediaUrl={mediaUrl} resolvedPreviewHref={resolvedPreviewHref} />;
    $[61] = mediaMimeType;
    $[62] = mediaUrl;
    $[63] = resolvedPreviewHref;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  const t12 = priceCurrencyText || undefined;
  let t13;
  if ($[65] !== ctaAriaLabel || $[66] !== displayTitle || $[67] !== hasPrice || $[68] !== hideActions || $[69] !== href || $[70] !== marketplaceBrand || $[71] !== priceValueText || $[72] !== resolvedPreviewHref || $[73] !== t12) {
    t13 = <MarketplaceFullFooter href={href} resolvedPreviewHref={resolvedPreviewHref} displayTitle={displayTitle} ctaAriaLabel={ctaAriaLabel} hideActions={hideActions} hasPrice={hasPrice} priceValueText={priceValueText} priceCurrencyText={t12} marketplaceBrand={marketplaceBrand} />;
    $[65] = ctaAriaLabel;
    $[66] = displayTitle;
    $[67] = hasPrice;
    $[68] = hideActions;
    $[69] = href;
    $[70] = marketplaceBrand;
    $[71] = priceValueText;
    $[72] = resolvedPreviewHref;
    $[73] = t12;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] !== sharedCardProps || $[76] !== t10 || $[77] !== t11 || $[78] !== t13 || $[79] !== t9) {
    t14 = <div {...sharedCardProps} className={t9} data-testid="manifold-item-card">{t10}{t11}{t13}</div>;
    $[75] = sharedCardProps;
    $[76] = t10;
    $[77] = t11;
    $[78] = t13;
    $[79] = t9;
    $[80] = t14;
  } else {
    t14 = $[80];
  }
  let t15;
  if ($[81] !== href || $[82] !== t14 || $[83] !== variant) {
    t15 = <LinkPreviewCardLayout href={href} variant={variant} hideActions={true}>{t14}</LinkPreviewCardLayout>;
    $[81] = href;
    $[82] = t14;
    $[83] = variant;
    $[84] = t15;
  } else {
    t15 = $[84];
  }
  return t15;
}